
import { HttpStatusCodes } from "@/constants/httpStatusCodes";
import { SUCCESS_POPUP_TYPE, WARNING_POPUP_TYPE } from "@/constants/popupTypes";
import { EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  ARCHIVED_TASK_STATUS,
  DOCUMENT_TASK_TYPE,
  FLOW_TASK_TYPE,
  GUIDE_TASK_TYPE,
  GUIDE_WITH_CUSTOM_BUTTON_TASK_TYPE,
  INFO_TASK_TYPE,
  ONBOARD_SPONSORED_EMPLOYEE_TASK_TYPE,
  REPORT_EMPLOYEES_NUMBER_CHANGE_TASK_TYPE,
  RIGHT_TO_WORK_CHECK_EMPLOYEE,
  RIGHT_TO_WORK_CHECK_EMPLOYER,
} from "@/constants/tasks";
import { CFormField, validateFormFields } from "@/models/form_cards";
import {
  SMApp,
  SMCases,
  SMHorizontalQuestionnaire,
  SMQuestionnaire,
  SMTasks,
  SMUser,
} from "@/store";
import { API_ROUTES } from "@/utils/API";
import { Component, Vue, Watch } from "vue-property-decorator";

enum aliasType {
  NAME_ALIAS = "name",
  EMAIL_ALIAS = "email",
  PASSWORD_ALIAS = "password",
}

const ALIAS = aliasType.EMAIL_ALIAS;

const COMPANY_PAGE_TITLE = "Company";
const EMPLOYEES_PAGE_TITLE = "Employees";
const MY_PROFILE_PAGE_TITLE = "My Profile";

@Component({
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    // TaskDocumentTemplate: () => import("@/modules/Documents/TaskDocumentTemplate.vue"),
    // DocumentActionContainerCard: () =>
    //   import("@/modules/Documents/DocumentActionContainerCard.vue"),
    TaskRTWCTemplateEmployee: () => import("@/modules/RTWC/employee/TaskRTWCTemplate.vue"),
    TaskRTWCTemplateEmployer: () => import("@/modules/RTWC/employer/TaskRTWCTemplate.vue"),
    DocumentTaskTemplate: () => import("@/modules/Documents/DocumentTaskTemplate.vue"),
  },
})
export default class TaskTemplate extends Vue {
  loading = false;

  deletePopup = false;

  subscriptionExpiredPopup = false;

  successType = SUCCESS_POPUP_TYPE;
  warningPopupType = WARNING_POPUP_TYPE;

  alias = ALIAS;

  @Watch("candidateEmail.value")
  onUpdateCandidateEmailValue() {
    this.state.isEmailAlreadyRegistered = false;
  }

  candidateEmail = new CFormField(this.$tc("labels.candidateEmail")).addRuleByAlias(this.alias);

  get isCandidateEmailValid() {
    return validateFormFields([this.candidateEmail]);
  }

  get successPopup() {
    return SMTasks.successPopup;
  }

  closeSuccessPopup() {
    SMTasks.closeSuccessPopup();

    const previousPath = SMApp.previousRouterHistoryPath
      ? SMApp.previousRouterHistoryPath
      : SMUser.basePath;

    this.$router.push(previousPath);
  }

  get taskTemplate() {
    return SMTasks.taskTemplate;
  }

  get status() {
    return this.taskTemplate?.status;
  }

  get isActionsBlockHidden() {
    if (SMUser.isExpert) return false;

    if (SMUser.isEmployer && SMTasks.taskTemplate?.user_role === EMPLOYEE_ROLE) return false;

    return (
      this.status === ARCHIVED_TASK_STATUS || SMTasks.taskTemplate?.user_role !== SMUser.user?.role
    );
  }

  get showDeleteButton() {
    const hide = (SMUser.isEmployee || SMUser.isEmployer) && !SMCases.caseId;

    return !hide;
  }

  isChecked = false;

  get showReadTheTaskConfirmation() {
    return this.isGuideTask;
  }

  get confirmCheckboxClasses() {
    if (SMUser.isExpert) return {};

    return {
      ["checkbox_bold"]: true,
    };
  }

  get type() {
    return this.taskTemplate?.type;
  }

  get isOnboardingTask() {
    return this.type === ONBOARD_SPONSORED_EMPLOYEE_TASK_TYPE;
  }

  get isReportTask() {
    return this.type === REPORT_EMPLOYEES_NUMBER_CHANGE_TASK_TYPE;
  }

  get isDocumentTask() {
    return this.type === DOCUMENT_TASK_TYPE;
  }

  get isFlowTask() {
    return this.type === FLOW_TASK_TYPE;
  }

  get isGuideTask() {
    return this.type === GUIDE_TASK_TYPE;
  }

  get isGuideWithCustomButtonTask() {
    return this.type === GUIDE_WITH_CUSTOM_BUTTON_TASK_TYPE;
  }

  get isInfoTask() {
    return this.type === INFO_TASK_TYPE;
  }

  get isNotInfoTaks() {
    return !this.isInfoTask;
  }

  get isRTWCEmployeeTask() {
    return this.type === RIGHT_TO_WORK_CHECK_EMPLOYEE;
  }

  get isRTWCEmployerTask() {
    return this.type === RIGHT_TO_WORK_CHECK_EMPLOYER;
  }

  get showActionButtons() {
    if (this.isDocumentTask) return false;

    return this.isNotInfoTaks;
  }

  get otherTaskType() {
    return ![
      ONBOARD_SPONSORED_EMPLOYEE_TASK_TYPE,
      REPORT_EMPLOYEES_NUMBER_CHANGE_TASK_TYPE,
      DOCUMENT_TASK_TYPE,
      RIGHT_TO_WORK_CHECK_EMPLOYEE,
      RIGHT_TO_WORK_CHECK_EMPLOYER,
    ].includes(String(this.type));
  }

  get isActionsHidden() {
    return !this.isCandidateEmailValid;
  }

  get isSubscriptionExpired() {
    return !!SMUser.user?.is_compliance_blocked;
  }

  get subscriptionExpiredPopupText() {
    return SMUser.isEmployee
      ? "popup.subscriptionExpired.employeeText"
      : "popup.subscriptionExpired.employerText";
  }

  get isOkButtonDisabled() {
    if (this.showReadTheTaskConfirmation) return !this.isChecked;

    return false;
  }

  get isComplianceBlocked() {
    if (this.showReadTheTaskConfirmation) return !this.isChecked;

    return this.isSubscriptionExpired;
  }

  closeDeletePopup() {
    this.deletePopup = false;
  }

  openDeletePopup() {
    this.deletePopup = true;
  }

  closeSubscriptionExpiredPopup() {
    this.subscriptionExpiredPopup = false;
  }

  openSubscriptionExpiredPopup() {
    this.subscriptionExpiredPopup = true;
  }

  async onTaskDelete() {
    if (!this.taskTemplate) return;

    const { id } = this.taskTemplate;

    await SMTasks.archiveTableTask(id);

    this.closeDeletePopup();

    const defaultRoutePath = SMUser.basePath;

    if (window.history.length > 1) {
      this.$router.go(-1);
    } else {
      this.$router.push(defaultRoutePath);
    }
  }

  get okLabel() {
    if (this.isGuideWithCustomButtonTask) return this.taskTemplate?.button_name || "";

    if (this.isGuideTask) return this.$root.$t("caption.complete");

    return this.$root.$t("caption.start");
  }

  isOkButtonShow() {
    return !SMTasks.taskTemplate.hide_button;
  }

  get caseId(): number {
    const id = Number(localStorage.getItem("case_id"));

    return SMCases.currentCaseId || id;
  }

  setPageSectionName() {
    const targetUser = this.taskTemplate?.target_user;

    if (!targetUser) return;

    const { first_name, last_name } = targetUser;

    const name = `${first_name} ${last_name}`;

    if (!name) return;

    SMApp.setEmployeeName(name);

    const sectionName = ` > ${name}`;

    SMApp.setPageSection(sectionName);
  }

  async goToEmployeeProfileDocumentsPath(userId: number) {
    await this.$router.push(`${SMUser.basePath}/employees/${userId}/documents`);

    SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);
  }

  async goCompanyDocumentsPath() {
    await this.$router.push(`${SMUser.basePath}/company/documents`);

    SMApp.setPageTitle(COMPANY_PAGE_TITLE);
  }

  async goProfileDocumentsPath() {
    await this.$router.push(`${SMUser.basePath}/profile/documents`);

    SMApp.setPageTitle(MY_PROFILE_PAGE_TITLE);
  }

  async goToExpertCompanyEmployeeDocuments() {
    const { company_id } = this.$route.params;

    SMApp.setPageTitle(EMPLOYEES_PAGE_TITLE);

    this.setPageSectionName();

    const path = `${SMUser.basePath}/companies/${company_id}/employees/${SMTasks.taskTemplate.target_user_id}/documents`;

    await this.$router.push(path);
  }

  async goToExpertCompanyDocuments() {
    const { company_id } = this.$route.params;

    const path = `${SMUser.basePath}/companies/${company_id}/documents`;

    await this.$router.push(path);

    SMApp.setPageTitle(COMPANY_PAGE_TITLE);
  }

  async goToExpertCaseDocuments() {
    const path = `cases/${this.caseId}/documents/`;

    await this.$router.push(path);
  }

  async resolveGuideTask() {
    const { id } = this.taskTemplate;

    await SMTasks.finishTask(id);
  }

  async resolveGuideWithCustomButtonTask() {
    const url = this.taskTemplate?.button_url;

    if (!url) return;

    try {
      await this.$router.push(url);
    } catch {
      return;
    }
  }

  async resolveTaskFlowTask() {
    if (SMTasks.taskUserflow === null) return;

    try {
      await SMQuestionnaire.openQuestionnaire(SMTasks.taskUserflow);
    } catch (e) {
      console.log("resolveTaskFlowTask error:", e);

      console.warn(`Task user flow is: ${SMTasks.taskUserflow}`);
    }
  }

  async resolveDocumentTask() {
    if (SMUser.isEmployer) {
      if (
        this.taskTemplate?.target_user.role === EMPLOYEE_ROLE ||
        this.taskTemplate?.user_role === EMPLOYEE_ROLE
      ) {
        const id = this.taskTemplate.target_user.ID || this.taskTemplate.user_id;

        if (!id) return console.log("No employee ID");

        await this.goToEmployeeProfileDocumentsPath(id);
      } else {
        await this.goCompanyDocumentsPath();
      }
    } else if (SMUser.isEmployee) {
      await this.goProfileDocumentsPath();
    } else if (SMUser.isExpert) {
      if (this.$route.params.company_id) {
        // TODO: DEV-9389. Return when @Abduchakor make implementation on back for check if it is company task;
        if (
          SMTasks.taskTemplate?.target_user_id &&
          SMTasks.taskTemplate?.target_user.role !== EMPLOYER_ROLE
        ) {
          await this.goToExpertCompanyEmployeeDocuments();
        } else {
          await this.goToExpertCompanyDocuments();
        }
      } else {
        console.log("No company ID");
      }
    } else {
      console.log("Current role has not access!");
    }
  }

  async onTaskOk() {
    if (this.isSubscriptionExpired) {
      this.openSubscriptionExpiredPopup();
      return;
    }

    try {
      this.loading = true;

      if (!this.taskTemplate) return;

      if (this.isGuideWithCustomButtonTask) await this.resolveGuideWithCustomButtonTask();

      if (this.isGuideTask) await this.resolveGuideTask();

      if (this.isFlowTask) await this.resolveTaskFlowTask();

      if (this.isDocumentTask) await this.resolveDocumentTask();
    } finally {
      this.loading = false;
    }
  }

  async sendInvitation() {
    try {
      this.loading = true;

      const { taskId } = this.$route.params;

      const { first_name, last_name } = this.employeeOnboardingForm;

      const body = {
        email: this.employeeOnboardingForm.email.value,
        first_name,
        last_name,
      };

      await this.axios.post(API_ROUTES.TASKS_FINISH_ONBOARD_EMPLOYEE(taskId), body);

      this.employeeOnboardingForm.email.value = "";
      this.employeeOnboardingForm.first_name = "";
      this.employeeOnboardingForm.last_name = "";

      SMTasks.openSuccessPopup();
    } catch (error) {
      if (error?.response?.status === HttpStatusCodes.INTERNAL_SERVER_ERROR) {
        this.state.isEmailAlreadyRegistered = true;
      }
    } finally {
      this.loading = false;
    }
  }

  openHorizontalQuestionnaire() {
    if (this.isSubscriptionExpired) {
      this.openSubscriptionExpiredPopup();
      return;
    }

    SMHorizontalQuestionnaire.open();

    const { id } = this.$route.params;

    SMHorizontalQuestionnaire.setTaskId(parseInt(id));
  }

  async getTaskTemplate() {
    const id = Number(this.$route.params.taskId ?? 0);

    await SMTasks.getTaskTemplate(id);

    if (!this.taskTemplate) return;

    const { name } = this.taskTemplate;

    if (name) SMApp.setPageTitle(name);
  }

  @Watch("employeeOnboardingForm.email.value")
  onUpdateEmployeeEmailValue() {
    this.state.isEmailAlreadyRegistered = false;
  }

  employeeOnboardingForm = {
    email: new CFormField(this.$tc("labels.employeeEmail")).addRuleByAlias("email"),
    first_name: "",
    last_name: "",
  };

  get isEmployeeOnboardingFormDisabled() {
    return (
      !this.isEmployeeEmailValid ||
      !this.employeeOnboardingForm.first_name ||
      !this.employeeOnboardingForm.last_name
    );
  }

  state = {
    isEmailAlreadyRegistered: false,
  };

  get isEmployeeEmailValid() {
    return validateFormFields([this.employeeOnboardingForm.email]) && !this.isComplianceBlocked;
  }

  created() {
    SMTasks.closeSuccessPopup();

    this.getTaskTemplate();
  }

  beforeDestroy() {
    SMTasks.unsetTaskTemplate();
  }
}
